// ============================================================
// Configuração visual do mapa (Nossa Cidade)
// ============================================================
// Metadados de categoria e de planos de impulsionamento + helpers puros.
// Nenhuma regra de negócio é definida aqui — apenas a camada de apresentação.
// A autoridade dos estados continua no backend (hasPromotion/isBoosted).
//
// O endpoint /api/businesses expõe `boostPlan` (nome do plano ativo em
// boost_plans, ex.: "Diário") e `hours` (horários) — ambos usados aqui.

export const CATEGORY_LABELS = {
  mercado: 'Mercado',
  farmacia: 'Farmácia',
  padaria: 'Padaria',
  restaurante: 'Restaurante',
  servicos: 'Serviços',
  evento: 'Eventos',
  publico: 'Público',
  mercado_publico: 'Mercado Público',
};

export function categoryLabel(category) {
  return CATEGORY_LABELS[category] || category || 'Comércio';
}

// Planos de impulsionamento (boost_plans). priority: menor = mais destaque
// (ordem de empilhamento no mapa). color = tom do anel/halo. seal = selo.
export const BOOST_PLANS = {
  flash: { key: 'flash', label: 'Flash', color: '#f59e0b', seal: '⚡', priority: 6 },
  daily: { key: 'daily', label: 'Diário', color: '#fb923c', seal: '☀', priority: 5 },
  bronze: { key: 'bronze', label: 'Bronze', color: '#b45309', seal: 'B', priority: 4 },
  silver: { key: 'silver', label: 'Prata', color: '#64748b', seal: 'P', priority: 3 },
  gold: { key: 'gold', label: 'Ouro', color: '#d97706', seal: 'O', priority: 2 },
  diamond: { key: 'diamond', label: 'Diamante', color: '#0ea5e9', seal: '◆', priority: 1 },
};

// Destaque genérico (quando só isBoosted está disponível — sem plano).
const GENERIC_BOOST = {
  key: 'boosted',
  label: 'Destaque',
  color: '#7c3aed',
  seal: '★',
  priority: 0,
  generic: true,
};

// Nome do plano (boost_plans.name) → chave interna de BOOST_PLANS.
const PLAN_NAME_TO_KEY = {
  flash: 'flash',
  diário: 'daily', diario: 'daily',
  bronze: 'bronze',
  prata: 'silver',
  ouro: 'gold',
  diamante: 'diamond',
};

function planByKey(key) {
  return key ? BOOST_PLANS[key] || null : null;
}

// Resolve a variação visual do marcador a partir dos dados do comércio.
// Retorna null para comércio comum (estático), ou um objeto de destaque.
export function resolveBoostVisual(business = {}) {
  const boosted = Boolean(business.isBoosted);
  if (!boosted) {
    // promoção ativa SEM impulsionamento → destaque de promoção (pulso suave)
    if (business.hasPromotion) {
      return { boosted: false, promo: true, color: '#ef4444', seal: null, priority: 0 };
    }
    return null;
  }

  // 1) Plano ativo vindo da listagem (autoridade real).
  const boostPlan = business.boostPlan || business.boost_plan;
  if (boostPlan) {
    const plan = planByKey(PLAN_NAME_TO_KEY[String(boostPlan).toLowerCase()]);
    if (plan) return { boosted: true, promo: false, planKey: plan.key, label: plan.label, color: plan.color, seal: plan.seal, priority: plan.priority };
  }

  // 2) Fallback legado (businesses.plan_type — não é atualizado pela compra).
  const legacyPlan = planByKey(business.plan_type);
  if (legacyPlan) return { boosted: true, promo: false, planKey: legacyPlan.key, label: legacyPlan.label, color: legacyPlan.color, seal: legacyPlan.seal, priority: legacyPlan.priority };

  // 3) Destaque genérico (só isBoosted, sem plano resolvido).
  return { boosted: true, promo: false, planKey: 'boosted', label: GENERIC_BOOST.label, color: GENERIC_BOOST.color, seal: GENERIC_BOOST.seal, priority: GENERIC_BOOST.priority, generic: true };
}

// z-index de empilhamento no mapa: destaque acima dos comuns; planos maiores acima.
export function markerZIndex(visual) {
  if (!visual) return 10;
  const base = visual.boosted ? 1000 : 500;
  return base + (visual.boosted ? (7 - (visual.priority || 0)) * 10 : 0);
}

// Aberto agora a partir dos horários do estabelecimento (business_hours).
// Retorna true/false, ou null quando não há dados suficientes (não inventa).
export function isOpenNow(hours) {
  if (!Array.isArray(hours) || hours.length === 0) return null;
  const now = new Date();
  const dow = now.getDay(); // 0=dom … 6=sáb (mesmo contrato de business_hours)
  const today = hours.find((h) => Number(h.day_of_week) === dow);
  if (!today) return null;
  if (today.is_24h) return true;
  if (today.is_closed) return false;

  const toMin = (t) => {
    if (!t) return null;
    const [h, m] = String(t).split(':').map(Number);
    if (Number.isNaN(h) || Number.isNaN(m)) return null;
    return h * 60 + m;
  };
  const open = toMin(today.open_time);
  const close = toMin(today.close_time);
  if (open == null || close == null) return null;

  const mins = now.getHours() * 60 + now.getMinutes();
  if (close >= open) return mins >= open && mins < close;
  // vira a madrugada
  return mins >= open || mins < close;
}

// Formata distância (metros) — o backend só envia distance quando a busca usa
// lat/lng (filtro "Perto de mim").
export function formatDistance(meters) {
  if (meters == null || Number.isNaN(Number(meters))) return null;
  const m = Number(meters);
  if (m < 1000) return `${Math.round(m)} m`;
  return `${(m / 1000).toFixed(1).replace('.', ',')} km`;
}

// Tempo restante de uma promoção (valid_until é DATE no backend).
export function promoRemaining(validUntil) {
  if (!validUntil) return null;
  const end = new Date(`${String(validUntil).slice(0, 10)}T23:59:59`);
  const now = new Date();
  const diffMs = end.getTime() - now.getTime();
  if (diffMs <= 0) return { ended: true, label: 'Encerrada' };
  const days = Math.floor(diffMs / 86400000);
  if (days <= 0) return { ended: false, label: 'Termina hoje' };
  if (days === 1) return { ended: false, label: 'Termina amanhã' };
  return { ended: false, label: `${days + 1} dias restantes` };
}
